/**
 * Default Theme — Purposeful Workspace
 *
 * Clean, airy, information-first. Neutral header, crisp 1px borders.
 * Status colors are the only saturated elements.
 * Inter as the default typeface.
 */

import type { AppTheme } from './types';

export const defaultTheme: AppTheme = {
  name: 'default',
  displayName: 'Default',
  description: 'Clean, purposeful workspace with crisp borders',

  antdTheme: {
    token: {
      colorPrimary: '#111111',
      colorSuccess: '#30a46c',
      colorWarning: '#e5a100',
      colorError: '#e5484d',
      colorInfo: '#3b82f6',
      colorLink: '#3b82f6',

      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeightStrong: 600,

      // Spacing
      padding: 16,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginLG: 24,
      marginXL: 32,

      borderRadius: 8,
      borderRadiusLG: 10,
      borderRadiusSM: 6,
      borderRadiusXS: 4,

      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#fafafa',
      colorBorder: '#eeeeee',
      colorBorderSecondary: '#f5f5f5',

      colorText: '#222222',
      colorTextSecondary: '#777777',
      colorTextTertiary: '#bbbbbb',
      colorTextQuaternary: '#dddddd',

      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
      boxShadowSecondary: '0 1px 2px rgba(0, 0, 0, 0.03)',
    },

    components: {
      Layout: {
        headerBg: '#ffffff',
        headerColor: '#111111',
        headerHeight: 48,
        headerPadding: '0 20px',
        siderBg: '#ffffff',
        bodyBg: '#fafafa',
      },

      Button: {
        borderRadius: 6,
        borderRadiusLG: 8,
        borderRadiusSM: 6,
        controlHeight: 36,
        controlHeightLG: 42,
        controlHeightSM: 28,
        fontWeight: 500,
        primaryShadow: 'none',
      },

      Card: {
        borderRadius: 8,
        borderRadiusLG: 10,
        colorBgContainer: '#ffffff',
        boxShadowTertiary: 'none',
        padding: 20,
        paddingLG: 24,
      },

      Table: {
        headerBg: '#ffffff',
        headerColor: '#bbbbbb',
        headerBorderRadius: 8,
        borderRadius: 8,
        rowHoverBg: '#fafaff',
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },

      Input: {
        borderRadius: 6,
        controlHeight: 36,
        controlHeightLG: 42,
        paddingBlock: 8,
        paddingInline: 12,
      },

      Select: {
        borderRadius: 6,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Tag: {
        borderRadiusSM: 4,
        fontSizeSM: 12,
      },

      Modal: {
        borderRadiusLG: 12,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      },

      Drawer: {
        colorBgElevated: '#ffffff',
      },

      Badge: {
        fontSize: 12,
      },

      Breadcrumb: {
        fontSize: 13,
        itemColor: '#999999',
        linkColor: '#3b82f6',
      },

      Segmented: {
        borderRadius: 6,
        itemSelectedBg: '#111111',
        itemSelectedColor: '#ffffff',
      },

      Tabs: {
        itemColor: '#999999',
        itemSelectedColor: '#111111',
        itemHoverColor: '#555555',
      },

      Dropdown: {
        borderRadiusLG: 8,
      },

      Tooltip: {
        borderRadius: 6,
      },

      Divider: {
        colorSplit: '#f0f0f0',
      },
    },
  },

  cssVariables: {
    // Layout
    '--layout-header-bg': '#ffffff',
    '--layout-sidebar-bg': '#ffffff',
    '--layout-content-bg': '#fafafa',

    // Surface elevation
    '--surface-0': '#fafafa',
    '--surface-1': '#ffffff',
    '--surface-2': '#ffffff',
    '--surface-3': '#ffffff',

    // Text hierarchy
    '--text-primary': '#222222',
    '--text-secondary': '#777777',
    '--text-tertiary': '#bbbbbb',
    '--text-muted': '#dddddd',

    // Border progression
    '--border-default': '#eeeeee',
    '--border-subtle': '#f5f5f5',
    '--border-strong': '#dddddd',

    // Legacy
    '--border-color': '#eeeeee',
    '--border-radius': '8px',

    // Shadows
    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.03)',
    '--shadow-md': '0 1px 3px rgba(0, 0, 0, 0.04)',
    '--shadow-lg': '0 4px 16px rgba(0, 0, 0, 0.08)',

    // Accents
    '--accent-warm': '#e5a100',
    '--accent-cool': '#3b82f6',

    // Status
    '--status-success': '#30a46c',
    '--status-warning': '#e5a100',
    '--status-error': '#e5484d',
    '--status-info': '#3b82f6',

    // Status subtle
    '--status-success-subtle': '#ecfdf3',
    '--status-warning-subtle': '#fffbeb',
    '--status-error-subtle': '#fef2f2',
    '--status-info-subtle': '#eff6ff',

    // Controls
    '--control-bg': '#fafafa',
    '--control-border': '#eeeeee',
    '--control-hover': '#f5f5f5',

    // Interactive
    '--hover-bg': '#f8f8f8',
    '--active-bg': '#f3f3f3',
  },
};
